import React, { ChangeEvent, ReactNode, useEffect, useState } from "react"
import { FlexBox } from "@components/wrapper"
import PhoneInput from "react-phone-number-input"
import clsx from "clsx"
import { useField } from "formik"
import ReactSelect, { MultiValue } from "react-select"
import CreatableSelect from "react-select/creatable"

import { SelectOption } from "@interfaces/misc"
import { Checkbox, Radio } from "@components/input/manual"

import "react-phone-number-input/style.css"
import { forwardInput } from "@components/input/manual"

interface FormFieldProps {
  label: string
  children: ReactNode
}

const FormField = ({ label, children }: FormFieldProps) => {
  return (
    <FlexBox className={clsx("sm:flex-row sm:gap-0", "flex-col gap-4")}>
      <div className={clsx("sm:w-1/3 font-medium text-sm sm:pr-[30px]", "w-full")}>
        {label}
        <span className="text-red">*</span>
      </div>

      <div className="sm:w-2/3 w-full text-sm">{children}</div>
    </FlexBox>
  )
}

interface InputProps {
  label: string
  name: string
  placeholder: string
}

const Input = ({ label, name, placeholder }: InputProps) => {
  const [field, meta] = useField(name)
  const errorText = meta.error

  return (
    <FormField label={label}>
      <div className="relative">
        <input
          {...field}
          placeholder={placeholder}
          className={clsx(
            "sm:py-3 px-4 rounded w-full text-sm border-2 border-transparent",
            "focus:outline-none focus:shadow-outline focus:border-green active:shadow-outline active:border-green",
            field.value && !errorText && "border-2 !border-green",
            errorText && "!border-red border",
            "py-2"
          )}
        />
        <small className="text-red absolute -bottom-4 left-0">{errorText}</small>
      </div>
    </FormField>
  )
}

const Phone = ({ label, name, placeholder }: InputProps) => {
  const [field, meta, form] = useField(name)
  const errorText = meta.error

  return (
    <FormField label={label}>
      <div className="relative phone_input">
        <PhoneInput
          inputComponent={forwardInput}
          defaultCountry="ID"
          value={field.value}
          placeholder={placeholder}
          onChange={(value) => {
            form.setValue(value)
          }}
          className={clsx(
            errorText && "phone_input--error",
            field.value && !errorText && "border-2 !border-green"
          )}
        />
        <small className="text-red absolute -bottom-4 left-0">{errorText}</small>
      </div>
    </FormField>
  )
}

const TextArea = ({ label, name, placeholder }: InputProps) => {
  const [field, meta] = useField(name)
  const errorText = meta.error
  const val = field.value as string

  return (
    <FormField label={label}>
      <div className="relative">
        <textarea
          {...field}
          rows={4}
          placeholder={placeholder}
          maxLength={500}
          className={clsx(
            "py-3 px-4 rounded w-full text-sm border-2 border-transparent",
            "focus:outline-none focus:shadow-outline focus:border-green active:shadow-outline active:border-green",
            field.value && !errorText && "border-2 !border-green",
            errorText && "!border-red border"
          )}
        />
        <small className="absolute bottom-3 right-3 text-grey-97">{val ? val.length : 0}/500</small>
        <small className="text-red absolute -bottom-4 left-0">{errorText}</small>
      </div>
    </FormField>
  )
}

interface FileUploadProps {
  label: string
  placeholder: string
  fileState: File | null
  errorMessage: string
  onUpload: (e: ChangeEvent<HTMLInputElement>) => void
}

const FileUpload = ({ label, placeholder, fileState, onUpload, errorMessage }: FileUploadProps) => {
  return (
    <FlexBox className={clsx("sm:flex-row sm:gap-0", "flex-col gap-4")}>
      <div className={clsx("sm:w-1/3 font-medium text-sm sm:pr-[30px]", "w-full")}>
        <div>
          {label} <span className="text-grey-97">(pdf)</span>
          <span className="text-red">*</span>
        </div>
        <small className="text-grey-97">Maksimal ukuran file 5MB</small>
      </div>

      <div className="sm:w-2/3 w-full text-sm">
        <FlexBox
          className={clsx(
            "w-full bg-white sm:py-2.5 sm:px-5 rounded sm:gap-5 border-2 border-transparent",
            errorMessage && "!border-red",
            "p-1 gap-4",
            fileState && !errorMessage && "border-2 !border-green"
          )}
          align="center"
        >
          <input type="file" hidden accept="application" id="file_picker" onChange={onUpload} />
          <label
            htmlFor="file_picker"
            className={clsx(
              "text-sm bg-green text-white font-semibold py-1.5 sm:px-30px rounded cursor-pointer",
              "px-3"
            )}
          >
            Pilih File
          </label>
          {!fileState ? (
            <div className="text-grey-90">{placeholder}</div>
          ) : (
            <div>{fileState.name}</div>
          )}
        </FlexBox>

        {errorMessage && <small className="text-red">{errorMessage}</small>}
      </div>
    </FlexBox>
  )
}

interface SelectProps {
  label: string
  name: string
  noOptionsMessage: string
  placeholder: string
  options: SelectOption[]
}

export const Select: React.FC<SelectProps> = ({
  label,
  placeholder,
  noOptionsMessage,
  options,
  name,
}) => {
  const [field, meta, form] = useField(name)
  const errorText = meta.error

  const handleChange = (e: any) => {
    form.setValue(e.value)
  }
  return (
    <FormField label={label}>
      <ReactSelect
        noOptionsMessage={({ inputValue }) => (!inputValue ? "Mohon tunggu..." : noOptionsMessage)}
        className={clsx(field.value && !errorText && "border-2 !border-green rounded")}
        isSearchable
        placeholder={placeholder}
        options={options}
        onChange={handleChange}
        theme={(theme) => ({
          ...theme,
          borderRadius: 4,
          colors: {
            ...theme.colors,
            primary: "#0DAD8E",
            primary75: "rgba(13, 173, 142, 0.75)",
            primary50: "rgba(13, 173, 142, 0.5)",
            primary25: "rgba(13, 173, 142, 0.25)",
          },
        })}
        styles={{
          control: (base, state) => ({
            ...base,
            borderColor: errorText ? "#ef2547" : "transparent",
            borderWidth: "2px",
            minHeight: "0",
            boxShadow: `${state.isFocused && !errorText ? "0 0 0 2px #0dad8e" : "none"}`,
            backgroundColor: "white",
            "&:hover": {
              borderColor: errorText ? "#ef2547" : "transparent",
            },
          }),
          valueContainer: (base) => ({
            ...base,
            padding: "12px 16px",
          }),
          input: (base) => ({
            ...base,
            padding: 0,
            margin: 0,
          }),
          menu: (base) => ({
            ...base,
            paddingTop: "16px",
            paddingBottom: "12px",
            paddingLeft: "4px",
            paddingRight: "16px",
          }),
          menuList: (base) => ({
            ...base,
            "::-webkit-scrollbar": {
              width: "2px",
            },
            "::-webkit-scrollbar-track": {
              background: "#BDBDBD",
            },
            "::-webkit-scrollbar-thumb": {
              background: "#0DAD8E",
            },
            "::-webkit-scrollbar-thumb:hover": {
              background: "#0DAD8E",
            },
          }),
          option: (base) => ({
            ...base,
            padding: "6px 12px",
          }),
          placeholder: (base) => ({ ...base, color: "#909090", fontSize: "14px" }),
          indicatorSeparator: (provided) => ({
            ...provided,
            display: "none",
          }),
        }}
      />
      {errorText && <small className="text-red">{errorText}</small>}
    </FormField>
  )
}

export const CreatableSelectInput: React.FC<SelectProps> = ({
  label,
  placeholder,
  noOptionsMessage,
  options,
  name,
}) => {
  const [field, meta, form] = useField(name)
  const errorText = meta.error

  const handleChange = (e: any) => {
    form.setValue(e.value)
  }
  return (
    <FormField label={label}>
      <CreatableSelect
        noOptionsMessage={({ inputValue }) => (!inputValue ? "Mohon tunggu..." : noOptionsMessage)}
        className={clsx(field.value && !errorText && "border-2 !border-green rounded")}
        isSearchable
        placeholder={placeholder}
        options={options}
        onChange={handleChange}
        theme={(theme) => ({
          ...theme,
          borderRadius: 4,
          colors: {
            ...theme.colors,
            primary: "#0DAD8E",
            primary75: "rgba(13, 173, 142, 0.75)",
            primary50: "rgba(13, 173, 142, 0.5)",
            primary25: "rgba(13, 173, 142, 0.25)",
          },
        })}
        styles={{
          control: (base, state) => ({
            ...base,
            borderColor: errorText ? "#ef2547" : "transparent",
            borderWidth: "2px",
            minHeight: "0",
            boxShadow: `${state.isFocused && !errorText ? "0 0 0 2px #0dad8e" : "none"}`,
            backgroundColor: "white",
            "&:hover": {
              borderColor: errorText ? "#ef2547" : "transparent",
            },
          }),
          valueContainer: (base) => ({
            ...base,
            padding: "12px 16px",
          }),
          input: (base) => ({
            ...base,
            padding: 0,
            margin: 0,
          }),
          menu: (base) => ({
            ...base,
            paddingTop: "16px",
            paddingBottom: "12px",
            paddingLeft: "4px",
            paddingRight: "16px",
          }),
          menuList: (base) => ({
            ...base,
            "::-webkit-scrollbar": {
              width: "2px",
            },
            "::-webkit-scrollbar-track": {
              background: "#BDBDBD",
            },
            "::-webkit-scrollbar-thumb": {
              background: "#0DAD8E",
            },
            "::-webkit-scrollbar-thumb:hover": {
              background: "#0DAD8E",
            },
          }),
          option: (base) => ({
            ...base,
            padding: "6px 12px",
          }),
          placeholder: (base) => ({ ...base, color: "#909090", fontSize: "14px" }),
          indicatorSeparator: (provided) => ({
            ...provided,
            display: "none",
          }),
        }}
      />
      {errorText && <small className="text-red">{errorText}</small>}
    </FormField>
  )
}

interface MultiSelectValue {
  label: string
  value: number
}

const MultipleSelect: React.FC<SelectProps> = ({ options, placeholder, name, label }) => {
  const [field, meta, form] = useField(name)
  const errorText = meta.error
  const [isFilled, setIsFilled] = useState<boolean>(false)

  const handleChange = (e: MultiValue<SelectOption>) => {
    if (e.map((value: MultiSelectValue) => value.label).length !== 0) {
      setIsFilled(true)
    } else {
      setIsFilled(false)
    }

    form.setValue(e.map((value: MultiSelectValue) => value.label))
  }

  return (
    <FormField label={label}>
      <CreatableSelect
        isMulti
        className={clsx(isFilled && !errorText && "border-2 !border-green rounded")}
        onChange={handleChange}
        isClearable={false}
        hideSelectedOptions
        isSearchable
        placeholder={placeholder}
        options={options}
        styles={{
          control: (base, state) => ({
            ...base,
            borderColor: errorText ? "#ef2547" : "transparent",
            borderWidth: "2px",
            minHeight: "0",
            boxShadow: `${state.isFocused && !errorText ? "0 0 0 2px #0dad8e" : "none"}`,
            backgroundColor: "white",
            "&:hover": {
              borderColor: errorText ? "#ef2547" : "transparent",
            },
          }),
          valueContainer: (base) => ({
            ...base,
            padding: "10px 16px",
          }),
          input: (base) => ({
            ...base,
            padding: 0,
            margin: 0,
          }),
          menu: (base) => ({
            ...base,
            paddingTop: "16px",
            paddingBottom: "12px",
            paddingLeft: "4px",
            paddingRight: "16px",
          }),
          menuList: (base) => ({
            ...base,
            "::-webkit-scrollbar": {
              width: "2px",
            },
            "::-webkit-scrollbar-track": {
              background: "#BDBDBD",
            },
            "::-webkit-scrollbar-thumb": {
              background: "#0DAD8E",
            },
            "::-webkit-scrollbar-thumb:hover": {
              background: "#0DAD8E",
            },
          }),
          option: (base) => ({
            ...base,
            padding: "6px 12px",
          }),
          multiValue: (provided) => ({
            ...provided,
            color: "#FFFFFF",
            backgroundColor: "#0DAD8E",
            marginRight: 6,
            marginBottom: 4,
            borderRadius: "4px",
            fontSize: "10px",
          }),
          multiValueLabel: (provided) => ({
            ...provided,
            color: "#FFFFFF",
          }),
          multiValueRemove: (provided) => ({
            ...provided,
            "&:hover": {
              color: "#FFFFFF",
              backgroundColor: "#0DAD8E",
            },
          }),
          placeholder: (base) => ({ ...base, color: "#909090", fontSize: "14px" }),
          dropdownIndicator: (provided) => ({
            ...provided,
            display: "none",
          }),
          indicatorSeparator: (provided) => ({
            ...provided,
            display: "none",
          }),
        }}
      />
      {errorText && <small className="text-red">{errorText}</small>}
    </FormField>
  )
}

interface CheckRowProps {
  label: string
  name: string
  options: string[]
  hasCreatable?: boolean
  isNotRow?: boolean
}

const CheckRow: React.FC<CheckRowProps> = ({ label, name, hasCreatable, options, isNotRow }) => {
  const [isCreatableChecked, setIsCreatableChecked] = useState(false)
  const [creatableOption, setCreatableOption] = useState("")
  const [errorCreatable, setErrorCreatable] = useState("")

  const [field, meta, form] = useField(name)
  const errorText = meta.error
  const values = field.value as string[]

  useEffect(() => {
    if (!hasCreatable || !values) {
      return
    }

    if (isCreatableChecked) {
      if (!values.length) {
        form.setValue([creatableOption, ...values])
      } else {
        form.setValue([creatableOption, ...values.slice(1)])
      }
    } else {
      return
    }
  }, [creatableOption])

  function handleInput(e: ChangeEvent<HTMLInputElement>) {
    const inputValue = e.target.value

    if (!inputValue) {
      setCreatableOption("")
    } else {
      setCreatableOption(inputValue)
    }

    setErrorCreatable("")
  }

  function handleCheckbox(e: ChangeEvent<HTMLInputElement>) {
    const val = e.target.value

    if (!values || !values.includes(val)) {
      form.setValue([...values, val])
    } else {
      const filtered = values.filter((value) => {
        return value !== val
      })
      form.setValue(filtered)
    }
  }

  const handleOtherCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
    setIsCreatableChecked(!isCreatableChecked)
    if (!isCreatableChecked) {
      form.setValue([creatableOption, ...values])

      if (!creatableOption) {
        setErrorCreatable("*Wajib Diisi")
      }
    } else {
      const filtered = values.splice(1)
      form.setValue(filtered)
      setErrorCreatable("")
    }
  }

  return (
    <FlexBox className={clsx("sm:flex-row sm:gap-0", "flex-col gap-4")}>
      <div className={clsx("sm:w-1/3 font-medium text-sm sm:pr-[30px]", "w-full")}>
        <div>
          {label} <span className="text-red">*</span>
        </div>
        {errorText && <small className="text-red">{errorText}</small>}
      </div>

      <div className="sm:w-2/3 w-full text-sm">
        <FlexBox
          className={clsx(
            !isNotRow && "sm:justify-between sm:flex-row sm:gap-0 sm:items-center",
            "flex-col gap-4"
          )}
        >
          {options.map((value) => (
            <Checkbox key={value} id={value} value={value} onChange={handleCheckbox}>
              <span className="font-semibold">{value}</span>
            </Checkbox>
          ))}

          {hasCreatable && (
            <FlexBox align="center" className="relative">
              <Checkbox id={name} onChange={handleOtherCheckbox} value={creatableOption} />
              <input
                placeholder="Lainnya"
                className={clsx(
                  "py-3 px-4 rounded w-full text-sm border-2",
                  "focus:outline-none focus:shadow-outline active:shadow-outline",
                  errorCreatable ? "border-red" : "border-transparent"
                )}
                onChange={handleInput}
              />
              {errorCreatable && (
                <small className="absolute text-red left-7 -bottom-4">{errorCreatable}</small>
              )}
            </FlexBox>
          )}
        </FlexBox>
      </div>
    </FlexBox>
  )
}

interface RadioRowProps {
  label: string
  name: string
  options: string[]
}

const RadioRow: React.FC<RadioRowProps> = ({ label, name, options }) => {
  const [field, meta, form] = useField(name)
  const errorText = meta.error

  function handleRadio(e: ChangeEvent<HTMLInputElement>) {
    const val = e.target.value

    form.setValue(val)
  }

  return (
    <FlexBox className={clsx("sm:flex-row sm:gap-0", "flex-col gap-4")}>
      <div className={clsx("sm:w-1/3 font-medium text-sm sm:pr-[30px]", "w-full")}>
        <div>
          {label} <span className="text-red">*</span>
        </div>
        {errorText && <small className="text-red">{errorText}</small>}
      </div>

      <div className="sm:w-2/3 w-full text-sm">
        <FlexBox className={clsx("sm:flex-row sm:gap-8", "flex-col gap-4")}>
          {options.map((value) => (
            <Radio key={value} name={label} id={value} value={value} onChange={handleRadio}>
              <span className="font-semibold">{value}</span>
            </Radio>
          ))}
        </FlexBox>
      </div>
    </FlexBox>
  )
}

FormField.Input = Input
FormField.Phone = Phone
FormField.FileUpload = FileUpload
FormField.TextArea = TextArea
FormField.Select = Select
FormField.CreatableSelect = CreatableSelectInput
FormField.MultipleSelect = MultipleSelect
FormField.CheckRow = CheckRow
FormField.RadioRow = RadioRow

export default FormField
